import React, { useEffect, useMemo, useState } from "react";
import { IonContent, IonHeader, IonPage } from "@ionic/react";
import MobileAppBar from "../../components/AppBar/MobileAppBar";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { useShipmentManager, defaultDateRange } from "../../context/ShipmentManagerProvider";
import type { Order, Shipment, ItemSnapshot } from "../../types/DB2";
import {
  collectOrderDisplayData,
  getOrderStatus,
  OrderDisplayData,
  stripOrderPrefix,
} from "../../utils/ordersDisplay";
import { formatStatusLabel } from "../../utils/status";
import { endOfDay, format, startOfDay } from "date-fns";
import { useUserData } from "../../context/UserDataFetcher";
import { useHistory } from "react-router-dom";
import { usePrimaryFetchedData } from "../../context/PrimaryFetchedDataContext";
import DateRangePickerWrapper from "../../components/Calendar/DateRangePickerStyles";

type AggregatedOrder = {
  orderId: string;
  label: string;
  statusLabel: string;
  rawStatus?: string;
  totalIncluded: number;
  totalExcluded: number;
  shipments: Array<{
    shipment: Shipment;
    orderData: OrderDisplayData;
    shipmentDate: Date | null;
  }>;
  destinations: string[];
  lastShipmentDate: Date | null;
};

const toDateSafe = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const maybe = value as any;
  if (typeof maybe?.toDate === "function") {
    try {
      return maybe.toDate();
    } catch {
      return null;
    }
  }
  if (typeof value === "number" || typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const OrdersMobile: React.FC = () => {
  const { dateRange, setDateRange } = useShipmentManager();
  const { shipments: contextShipments, orders: contextOrders } = usePrimaryFetchedData();
  const userData = useUserData();
  const history = useHistory();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [showDatePicker, setShowDatePicker] = useState(false);
  const effectiveDateRange = useMemo(() => {
    if (dateRange?.startDate && dateRange?.endDate) return dateRange;
    return {
      startDate: new Date(defaultDateRange.startDate),
      endDate: new Date(defaultDateRange.endDate),
      key: defaultDateRange.key ?? "selection",
    };
  }, [dateRange]);

  useEffect(() => {
    if (userData?.role === "driver") {
      history.replace("/shipments");
    }
  }, [userData?.role, history]);

  if (userData?.role === "driver") {
    return null;
  }

  const sortedShipments = useMemo(() => {
    const arr = Array.isArray(contextShipments) ? [...contextShipments] : [];
    arr.sort((a, b) => {
      const ad = toDateSafe((a as any)?.shipmentDate)?.getTime() || 0;
      const bd = toDateSafe((b as any)?.shipmentDate)?.getTime() || 0;
      return bd - ad;
    });
    return arr;
  }, [contextShipments]);

  const filteredShipments = useMemo(() => {
    if (!effectiveDateRange?.startDate || !effectiveDateRange?.endDate) return sortedShipments;
    const start = startOfDay(effectiveDateRange.startDate);
    const end = endOfDay(effectiveDateRange.endDate);
    return sortedShipments.filter((shipment) => {
      const shipmentDate = toDateSafe((shipment as any)?.shipmentDate);
      if (!shipmentDate) return false;
      return shipmentDate >= start && shipmentDate <= end;
    });
  }, [sortedShipments, effectiveDateRange]);

  const aggregatedOrders = useMemo<AggregatedOrder[]>(() => {
    const shipmentsList = filteredShipments;

    if (userData?.role === "dealer") {
      const dealerOrders = Array.isArray(contextOrders) ? (contextOrders as Order[]) : [];
      const dealerId = userData?.dealerId ?? "";
      const aggregates = dealerOrders.map((order) => {
        const orderId = String(order?.id ?? (order as any)?.orderId ?? "");
        const shipmentsForOrder = shipmentsList.filter(
          (shipment) => shipment?.ordersSnapshot && shipment.ordersSnapshot[orderId]
        );

        let includedItems: ItemSnapshot[] = [];
        let excludedItems: ItemSnapshot[] = [];

        const shipmentsDetail = shipmentsForOrder.map((shipment) => {
          const orderDisplays = collectOrderDisplayData(shipment);
          const orderDisplay =
            orderDisplays.find((display) => display.orderId === orderId) ?? null;
          if (orderDisplay) {
            includedItems = includedItems.concat(orderDisplay.includedItems);
            excludedItems = excludedItems.concat(orderDisplay.excludedItems);
          }
          return {
            shipment,
            orderData:
              orderDisplay ?? {
                orderId,
                snapshot: null,
                includedItems: [],
                excludedItems: [],
                destinations: [],
                dealerLabel: dealerId || order.dealerId || "",
              },
            shipmentDate: toDateSafe((shipment as any)?.shipmentDate),
          };
        });

        if (!includedItems.length && order?.itemsSnapshot) {
          includedItems = Object.values(order.itemsSnapshot) as ItemSnapshot[];
        }

        const destinations = new Set<string>();
        shipmentsDetail.forEach(({ orderData }) => {
          orderData?.destinations?.forEach((dest) => {
            if (typeof dest === "string" && dest.trim()) destinations.add(dest.trim());
          });
        });
        if (Array.isArray((order as any)?.destinations)) {
          (order as any).destinations.forEach((dest: string) => {
            if (typeof dest === "string" && dest.trim()) destinations.add(dest.trim());
          });
        }

        const rawStatus: string | undefined = (order as any)?.status;
        const formattedStatus =
          rawStatus?.length ? formatStatusLabel(rawStatus) || rawStatus : "Unknown";

        const lastShipmentDate = shipmentsDetail.reduce<Date | null>((latest, detail) => {
          if (!detail.shipmentDate) return latest;
          if (!latest || detail.shipmentDate.getTime() > latest.getTime()) {
            return detail.shipmentDate;
          }
          return latest;
        }, null);

        return {
          orderId,
          label: stripOrderPrefix(orderId, shipmentsDetail[0]?.orderData?.snapshot),
          rawStatus,
          statusLabel: formattedStatus,
          totalIncluded: includedItems.length,
          totalExcluded: excludedItems.length,
          destinations: Array.from(destinations),
          shipments: shipmentsDetail,
          lastShipmentDate,
        } as AggregatedOrder;
      });

      return aggregates
        .filter((order) => order.shipments.length > 0 || order.totalIncluded > 0)
        .sort((a, b) => {
          const ad = a.lastShipmentDate?.getTime() || 0;
          const bd = b.lastShipmentDate?.getTime() || 0;
          return bd - ad;
        });
    }

    const map = new Map<string, AggregatedOrder>();
    shipmentsList.forEach((shipment) => {
      const displays = collectOrderDisplayData(shipment);
      const shipmentDate = toDateSafe((shipment as any)?.shipmentDate);
      displays.forEach((orderDisplay) => {
        const rawStatus = getOrderStatus(orderDisplay);
        const statusLabel =
          rawStatus && rawStatus.length
            ? formatStatusLabel(rawStatus) || rawStatus
            : "Unknown";
        const existing = map.get(orderDisplay.orderId);
        if (!existing) {
          map.set(orderDisplay.orderId, {
            orderId: orderDisplay.orderId,
            label: stripOrderPrefix(orderDisplay.orderId, orderDisplay.snapshot),
            rawStatus,
            statusLabel,
            totalIncluded: orderDisplay.includedItems.length,
            totalExcluded: orderDisplay.excludedItems.length,
            destinations: [...orderDisplay.destinations],
            shipments: [
              {
                shipment,
                orderData: orderDisplay,
                shipmentDate,
              },
            ],
            lastShipmentDate: shipmentDate,
          });
        } else {
          existing.totalIncluded += orderDisplay.includedItems.length;
          existing.totalExcluded += orderDisplay.excludedItems.length;
          existing.shipments.push({
            shipment,
            orderData: orderDisplay,
            shipmentDate,
          });
          existing.destinations = Array.from(
            new Set([...existing.destinations, ...orderDisplay.destinations])
          );
          if (!existing.rawStatus && rawStatus) {
            existing.rawStatus = rawStatus;
            existing.statusLabel = statusLabel;
          }
          if (
            shipmentDate &&
            (!existing.lastShipmentDate ||
              shipmentDate.getTime() > existing.lastShipmentDate.getTime())
          ) {
            existing.lastShipmentDate = shipmentDate;
          }
        }
      });
    });

    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [filteredShipments, contextOrders, userData?.role, userData?.dealerId]);

  const formatRange = () => {
    const { startDate, endDate } = (effectiveDateRange || {}) as any;
    if (!startDate || !endDate) return "Select Date Range";
    try {
      return `${format(startDate, "MMM d, yyyy")} - ${format(
        endDate,
        "MMM d, yyyy"
      )}`;
    } catch {
      return "Select Date Range";
    }
  };

  const formatShipmentDate = (date: Date | null) =>
    date ? format(date, "PP") : "No scheduled date";

  return (
    <IonPage>
      <IonHeader>
        <MobileAppBar
          title="Orders"
          position="static"
          actions={
            <Button
              variant="outlined"
              size="small"
              startIcon={<CalendarMonthIcon sx={{ color: "#fff" }} />}
              onClick={() => setShowDatePicker(true)}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.7)",
                textTransform: "none",
                lineHeight: 1.1,
                "&:hover": { borderColor: "#fff" },
                maxWidth: "70vw",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {formatRange()}
            </Button>
          }
        />
      </IonHeader>
      <IonContent>
        <Box sx={{ px: 1, pt: 1 }}>
          <List disablePadding>
            {aggregatedOrders.map((order) => {
              const isOpen = !!expanded[order.orderId];
              return (
                <Box
                  key={order.orderId}
                  sx={{ mb: 0.5, borderBottom: "1px solid", borderColor: "divider" }}
                >
                  <ListItem
                    disableGutters
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() =>
                          setExpanded((prev) => ({
                            ...prev,
                            [order.orderId]: !isOpen,
                          }))
                        }
                      >
                        {isOpen ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <AssignmentIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ pr: 5 }}
                        >
                          <Typography
                            variant="subtitle1"
                            color="text.primary"
                            sx={{ fontWeight: 600 }}
                          >
                            Order {order.label}
                          </Typography>
                          <Chip
                            size="small"
                            label={order.statusLabel}
                            color="info"
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            label={`${order.shipments.length} shipments`}
                            color="default"
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            label={`${order.totalIncluded} items`}
                            variant="outlined"
                          />
                        </Stack>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          Destinations:{" "}
                          {order.destinations.length
                            ? order.destinations.join(", ")
                            : "None"}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 2, pr: 1, pb: 1 }}>
                      <List disablePadding>
                        {order.shipments.map(({ shipment, orderData, shipmentDate }) => (
                          <Box
                            key={`${shipment.id}-${orderData.orderId}`}
                            sx={{
                              mb: 1,
                              pb: 1,
                              borderBottom: "1px dashed",
                              borderColor: "divider",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 0.5 }}
                            >
                              <LocalShippingIcon fontSize="small" color="action" />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Shipment {shipment.id}
                              </Typography>
                              <Chip
                                size="small"
                                label={formatShipmentDate(shipmentDate)}
                                variant="outlined"
                              />
                              <Chip
                                size="small"
                                color="info"
                                label={
                                  formatStatusLabel(shipment.status) || shipment.status
                                }
                                variant="outlined"
                              />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Destinations:{" "}
                              {orderData.destinations.length
                                ? orderData.destinations.join(", ")
                                : "No destinations recorded"}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Included: ${orderData.includedItems.length}`}
                              />
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Excluded: ${orderData.excludedItems.length}`}
                              />
                            </Stack>
                            {orderData.includedItems.length > 0 && (
                              <Box sx={{ pl: 2 }}>
                                {orderData.includedItems.map((item) => (
                                  <Typography
                                    key={item.id}
                                    variant="caption"
                                    display="block"
                                    color="text.secondary"
                                  >
                                    • {item.description || item.modelName || item.id}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                        ))}
                      </List>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
            {aggregatedOrders.length === 0 && (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No orders in the selected range.
                </Typography>
              </Box>
            )}
          </List>
        </Box>
        <Drawer anchor="bottom" open={showDatePicker} onClose={() => setShowDatePicker(false)}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Select Date Range
            </Typography>
            <DateRangePickerWrapper>
              <DateRangePicker
                onChange={(item: any) => {
                  const range = item.range1 || item.selection || {};
                  const nextStart = range.startDate
                    ? startOfDay(range.startDate)
                    : effectiveDateRange.startDate;
                  const nextEnd = range.endDate
                    ? endOfDay(range.endDate)
                    : effectiveDateRange.endDate;
                  setDateRange({
                    startDate: nextStart,
                    endDate: nextEnd,
                    key: range.key || "selection",
                  });
                }}
                showPreview={false}
                moveRangeOnFirstSelection={false}
                months={1}
                ranges={[
                  {
                    startDate: effectiveDateRange.startDate,
                    endDate: effectiveDateRange.endDate,
                    key: effectiveDateRange.key ?? "selection",
                  },
                ]}
                showDateDisplay={false}
                staticRanges={[]}
                inputRanges={[]}
                direction="horizontal"
              />
            </DateRangePickerWrapper>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button variant="contained" onClick={() => setShowDatePicker(false)}>
                Done
              </Button>
            </Box>
          </Box>
        </Drawer>
      </IonContent>
    </IonPage>
  );
};

export default OrdersMobile;
